import type { ComponentProps, ReactNode } from 'react';
import React from 'react';
import styled from '@emotion/styled';
import type { BackgroundColorProps, HeightProps, MinWidthProps, SpaceProps } from 'styled-system';
import { backgroundColor, height, minWidth, space } from 'styled-system';

import { Flex } from 'common/components/Flex';
import Icon from 'common/components/Icon';
import { HeadlineH8 } from 'common/components/Typography';
import { focusOutlineCSS } from 'common/theme/styles';
import type { FillProps } from 'common/theme/system-types';
import { cursor, fill, transform, transition } from 'common/theme/system-utilities';
import type { CursorProps, TransformProps, TransitionProps } from 'common/theme/system-utilities';

const ICON_SIZE = 14;

interface FilterAccordionSectionProps {
  title: string | ReactNode;
  isExpanded: boolean;
  onToggle: () => void;
  children: ReactNode;
  sectionKey: string;
}

const containerStyles: ComponentProps<typeof Flex> = {
  flexDirection: 'column',
  width: 1,
};

const HeaderButton = styled(Flex)<CursorProps & React.ButtonHTMLAttributes<HTMLButtonElement>>`
  ${cursor};
  ${focusOutlineCSS};
`;

const headerButtonStyles: Partial<ComponentProps<typeof HeaderButton>> = {
  alignItems: 'center',
  justifyContent: 'space-between',
  py: 'sp_16',
  px: 'sp_16',
  backgroundColor: 'transparent',
  border: 'none',
  cursor: 'pointer',
  height: '58px',
};

const titleStyles: Partial<ComponentProps<typeof HeadlineH8>> = {
  mr: ['sp_16', null, null, 'sp_20'],
  color: 'sys.neutral.text.strong',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  as: 'span',
};

const ArrowIcon = styled(Icon)<TransformProps & FillProps & TransitionProps & MinWidthProps>`
  ${transform};
  ${fill};
  ${transition};
  ${minWidth};
`;

const arrowIconStyles: Partial<ComponentProps<typeof ArrowIcon>> = {
  fill: 'sys.neutral.icon.default',
  transition: 'transform 0.1s ease',
  size: ICON_SIZE,
  minWidth: ICON_SIZE,
};

const Divider = styled.div<SpaceProps & HeightProps & BackgroundColorProps>`
  ${space};
  ${height};
  ${backgroundColor};
`;

const dividerStyles: ComponentProps<typeof Divider> = {
  mx: 'sp_4',
  height: '1px',
  backgroundColor: 'sys.neutral.border.weak',
};

const FilterAccordionSection = ({
  title,
  isExpanded,
  onToggle,
  children,
  sectionKey,
}: FilterAccordionSectionProps) => {
  const buttonId = `accordion-button-${sectionKey}`;
  const contentId = `accordion-content-${sectionKey}`;

  return (
    <Flex {...containerStyles} data-testid={`filter-section-${sectionKey}`} as="section">
      <HeaderButton
        {...headerButtonStyles}
        as="button"
        onClick={onToggle}
        aria-expanded={isExpanded}
        aria-controls={contentId}
        id={buttonId}
        data-testid="accordion-title"
        type="button"
      >
        <HeadlineH8 {...titleStyles} as="h3">
          {title}
        </HeadlineH8>
        <ArrowIcon
          {...arrowIconStyles}
          name="arrowDownStrong"
          transform={`rotate(${isExpanded ? 180 : 0}deg)`}
          aria-hidden="true"
        />
      </HeaderButton>
      {isExpanded && (
        <Flex
          id={contentId}
          role="region"
          aria-labelledby={buttonId}
          pt="sp_8"
          pb="sp_16"
          flexDirection="column"
        >
          {children}
        </Flex>
      )}
      <Divider {...dividerStyles} />
    </Flex>
  );
};

export default FilterAccordionSection;
